<template>
  <div class="p-4 pt-2">
    <v-alert color="blue" type="info" outlined dense class="text-sm font-medium mb-4">
      Link each Charge / VAS type to the account-management ledger that should receive the posting.
    </v-alert>

    <!-- Search / filter bar -->
    <v-text-field
      v-model="search"
      prepend-inner-icon="mdi-magnify"
      label="Search charge type…"
      outlined
      dense
      clearable
      class="mb-4"
      hide-details
    />

    <!-- Header row -->
    <div class="vas-header d-flex align-center px-3 py-2 mb-1 rounded">
      <span class="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wide"
        >Charge Type</span
      >
      <span class="vas-col text-xs font-semibold text-gray-500 uppercase tracking-wide"
        >Account / Ledger</span
      >
    </div>

    <!-- Rows -->
    <v-card outlined class="overflow-hidden">
      <div
        v-for="(row, index) in filteredRows"
        :key="row.key"
        class="vas-row d-flex align-center px-3 py-1"
        :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
      >
        <span class="flex-1 text-sm text-gray-700 py-1 pr-4">{{ row.label }}</span>

        <div class="vas-col">
          <div
            v-if="row.accountId && row.accountId.name && !editModes[row.key]"
            class="d-flex align-center border rounded px-3 py-1 bg-gray-50 h-[36px]"
          >
            <span class="flex-grow-1 text-sm font-semibold truncate">{{ row.accountId.name }}</span>
            <v-btn icon x-small @click="$set(editModes, row.key, true)" class="mr-1">
              <v-icon x-small color="grey darken-1">mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon x-small @click="clearRowAccount(row.key)">
              <v-icon x-small color="red darken-1">mdi-close</v-icon>
            </v-btn>
          </div>
          <v-autocomplete
            v-else
            :value="row.accountId"
            @input="onAccountChange(row.key, $event)"
            :items="accountsList"
            :search-input.sync="searchTerms[row.key]"
            @update:search-input="(val) => val && val.length > 1 && debouncedFetchAccounts(val)"
            return-object
            item-text="name"
            item-value="_id"
            outlined
            dense
            hide-details
            clearable
            placeholder="Search account…"
            :loading="accountsLoading"
            :filter="() => true"
            no-data-text="Type to search accounts…"
            class="compact-select"
          >
            <template #item="{ item }">
              <div class="d-flex flex-column py-1">
                <span class="text-subtitle-2">{{ item.name }}</span>
                <span class="text-xs text-gray-400 uppercase tracking-wider">{{
                  item.category
                }}</span>
              </div>
            </template>
          </v-autocomplete>
        </div>
      </div>

      <div v-if="filteredRows.length === 0" class="pa-6 text-center text-gray-400 text-sm">
        No charge types match your search.
      </div>
    </v-card>
  </div>
</template>

<script>
import { axiosUrl } from '@/utils/workforceApi'

const DEFAULT_ROWS = [
  { key: 'rto_charges', label: 'RTO Charges' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'seize_charges', label: 'Seize Charges' },
  { key: 'legal_charges', label: 'Legal Charges' },
  { key: 'agreement_charges', label: 'Agreement Charges' },
  { key: 'other_charges', label: 'Other Charges' },
  { key: 'bounce_charges', label: 'Bounce Charges' },
  { key: 'co_lending_documentation', label: 'Co-Lending Documentation' },
  { key: 'processing_fees', label: 'Processing Fees' },
  { key: 'service_tax', label: 'Service Tax' },
  { key: 'stamp_duty', label: 'Stamp Duty' },
  { key: 'dealer_subvention', label: 'Dealer Subvention' },
  { key: 'default_charges', label: 'Default Charges' },
  { key: 'recovery_charges', label: 'Recovery Charges' },
  { key: 'foreclosure_charges', label: 'Foreclosure Charges' },
  { key: 'part_payment_charges', label: 'Part Payment Charges' },
  { key: 'cheque_nach_swapping_charges', label: 'Cheque / NACH Swapping Charges' },
  { key: 'cheque_nach_bouncing_charges', label: 'Cheque / NACH Bouncing Charges' },
  { key: 'first_noc_issuance_charges', label: 'First NOC Issuance Charges' },
  { key: 'duplicate_noc_charges', label: 'Duplicate NOC Charges' },
  { key: 'loan_re_schedulement_charges', label: 'Loan Re-Schedulement Charges' },
  { key: 'valuation_charges', label: 'Valuation Charges' },
  { key: 'title_search_report_charges', label: 'Title Search Report Charges (Legal Changes)' },
  { key: 'cibil', label: 'CIBIL' },
  { key: 'nach', label: 'NACH' },
  { key: 'statement_of_accounts', label: 'Statement Of Accounts' },
  { key: 'late_payment_charges', label: 'Late Payment Charges' },
  { key: 'cash_deposition_charges', label: 'Cash Deposition Charges' },
  { key: 're_schedulement_of_emi_date', label: 'Re-Schedulement of EMI Date' },
  { key: 'document_deposit_charges', label: 'Document Deposit Charges' },
  { key: 'duplicate_repayment_schedule_charges', label: 'Duplicate Repayment Schedule Charges' },
  { key: 'inspection_charges', label: 'Inspection Charges' },
  { key: 'franking_charges', label: 'Franking Charges' },
  { key: 'misc_charges', label: 'Misc Charges' },
  { key: 'mortgage_charges', label: 'Mortgage Charges' },
  { key: 'cersai_charges', label: 'CERSAI Charges' },
  { key: 'broken_interest', label: 'Broken Interest' },
  { key: 'upfront_fees', label: 'Upfront Fees' },
  { key: 'pre_emi', label: 'Pre EMI' },
  { key: 'customer_insurance', label: 'Customer Insurance' },
  { key: 'interest_on_partial_disbursements', label: 'Interest on Partial Disbursements' },
  { key: 'membership_card_fee', label: 'Membership Card Fee' },
  { key: 'life_insurance_1', label: 'Life Insurance 1' },
  { key: 'life_insurance_2', label: 'Life Insurance 2' },
  { key: 'hospital_plus_1', label: 'Hospital Plus 1' },
  { key: 'hospital_plus_2', label: 'Hospital Plus 2' },
  { key: 'other_charges_1', label: 'Other Charges 1' },
  { key: 'other_charges_2', label: 'Other Charges 2' },
  { key: 'postal_charges', label: 'Postal Charges' },
  { key: 'telegram_charges', label: 'Telegram Charges' },
  { key: 'pre_payment_charges', label: 'Pre-Payment Charges' },
  { key: 'property_insurance', label: 'Property Insurance' },
  { key: 'custodian_charges', label: 'Custodian Charges' },
  { key: 'document_retrieval_charges', label: 'Document Retrieval Charges' },
  { key: 'disbursement_cancellation_charges', label: 'Disbursement Cancellation Charges' },
  { key: 'additional_interest', label: 'Additional Interest' },
  { key: 'advance_interest', label: 'Advance Interest' },
  { key: 'third_party_transfer', label: 'Third Party Transfer' },
  { key: 'bsp_participation', label: 'BSP Participation' },
  { key: 'emi_forwarding', label: 'EMI Forwarding' },
  { key: 'share_application_money', label: 'Share Application Money' },
  { key: 'lpc_od_interest', label: 'LPC OD Interest' },
  { key: 'commitment_fee', label: 'Commitment Fee' },
  { key: 'moratorium_interest', label: 'Moratorium Interest' },
  { key: 'registration_charges', label: 'Registration Charges' },
  { key: 'write_back', label: 'Write Back' },
  { key: 'rc_book_charges', label: 'RC Book Charges' },
  { key: 'incentive_1', label: 'Incentive 1' },
  { key: 'incentive_2', label: 'Incentive 2' },
  { key: 'commission_1', label: 'Commission 1' },
  { key: 'commission_2', label: 'Commission 2' },
  { key: 'npa_charges', label: 'NPA Charges' },
  { key: 'loan_protection_policy', label: 'Loan Protection Policy' },
  { key: 'declaration_cum_affidavit', label: 'Declaration Cum Affidavit' },
  { key: 'deposit_of_title_deeds', label: 'Deposit Of Title Deeds' },
  { key: 'loan_pre_emi', label: 'Loan Pre EMI' },
  { key: 'device_charges', label: 'Device Charges' },
  { key: 'credit_admin_charges', label: 'Credit Admin Charges' },
  { key: 'membership_fees', label: 'Membership Fees' },
  { key: 'printing_stationary_charges', label: 'Printing & Stationary Charges' },
  { key: 'incidental_charges', label: 'Incidental Charges' },
  { key: 'sms_charges', label: 'SMS Charges' },
  { key: 'lender_insurance', label: 'Lender Insurance' },
  { key: 'self_insurance', label: 'Self Insurance' },
  { key: 'document_charges', label: 'Document Charges' },
  { key: 'foreclosure_letter_charges', label: 'Foreclosure Letter Charges' },
  { key: 'lod_letter_charges', label: 'LOD Letter Charges' },
  { key: 'noc_letter_charges', label: 'NOC Letter Charges' },
  { key: 'life_insurance', label: 'Life Insurance' },
  { key: 'general_insurance', label: 'General Insurance' },
  { key: 'termination_amount_receipt', label: 'Termination Amount (Receipt)' },
  { key: 'dealer_commission_payable', label: 'Dealer Commission (Payable)' },
  { key: 'loan_suraksha_insurance_r', label: 'Loan Suraksha Insurance (R)' },
  { key: 'sale_amount_repossession', label: 'Sale Amount (Repossession)' },
  { key: 'interest_subvention_from_oem', label: 'Interest Subvention from OEM' },
  { key: 'reschedulement_charges', label: 'Reschedulement Charges' },
  { key: 'general_subvention', label: 'General Subvention' },
  { key: 'hospi_cash_insurance', label: 'Hospi Cash Insurance' },
  { key: 'life_cycle_management_fees', label: 'Life Cycle Management Fees' },
  { key: 'application_fee', label: 'Application Fee' },
  { key: 'non_instrumental_charges', label: 'Non-Instrumental Charges' },
  { key: 'technical_charges', label: 'Technical Charges' },
  { key: 'oem_subvention', label: 'OEM Subvention' },
  { key: 'security_deposit', label: 'Security Deposit' },
  { key: 'legal_fee', label: 'Legal Fee' },
  { key: 'technical_fee', label: 'Technical Fee' },
  { key: 'tranche_pre_emi', label: 'Tranche Pre-EMI' },
  { key: 'handling_charges', label: 'Handling Charges' },
  { key: 'rcu_charges', label: 'RCU Charges' },
  { key: 'disbursement_charges', label: 'Disbursement Charges' },
  { key: 'late_payment_fee', label: 'Late Payment Fee' }
]

export default {
  name: 'VASPostingSettings',

  props: {
    value: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      // Guard to break the value ↔ rows watcher cycle
      syncingFromProp: false,

      // ── Rows: only key + accountId. No search state here. ─────────────────
      rows: this.buildRows(this.value),

      // ── Search terms keyed by row.key. NOT watched for emit. ──────────────
      searchTerms: {},

      // ── Global filter ─────────────────────────────────────────────────────
      search: '',

      // ── Account autocomplete shared list ──────────────────────────────────
      accountsList: [],
      accountsLoading: false,
      editModes: {}
    }
  },

  computed: {
    filteredRows() {
      if (!this.search) return this.rows
      const q = this.search.toLowerCase()
      return this.rows.filter((r) => r.label.toLowerCase().includes(q))
    }
  },

  watch: {
    // Parent pushed a new saved value (e.g. loaded subCase)
    value: {
      deep: true,
      handler(val) {
        if (this.syncingFromProp) return
        this.syncingFromProp = true
        this.rows = this.buildRows(val)
        this.$nextTick(() => {
          this.syncingFromProp = false
        })
      }
    },

    // rows changes (only accountId mutations matter) → emit to parent
    rows: {
      deep: true,
      handler(val) {
        if (this.syncingFromProp) return
        this.$emit(
          'input',
          val.map((r) => ({ key: r.key, accountId: r.accountId }))
        )
      }
    }
  },

  created() {
    // Instance-specific debounce – avoids shared-state issues
    let t
    this.debouncedFetchAccounts = (val) => {
      clearTimeout(t)
      t = setTimeout(() => this.fetchAccounts(val), 400)
    }
  },

  async mounted() {
    await this.fetchAccounts()
  },

  methods: {
    onAccountChange(key, val) {
      const row = this.rows.find((r) => r.key === key)
      if (row) {
        if (val && typeof val === 'object') {
          this.$set(row, 'accountId', { _id: val._id, name: val.name })
          this.$set(this.editModes, key, false)
        } else {
          this.$set(row, 'accountId', val)
        }
      }
    },

    clearRowAccount(key) {
      const row = this.rows.find((r) => r.key === key)
      if (row) {
        this.$set(row, 'accountId', null)
        this.$set(this.editModes, key, false)
      }
    },

    buildRows(savedArray) {
      const savedMap = {}
      if (Array.isArray(savedArray)) {
        savedArray.forEach((s) => {
          if (s && s.key) {
            savedMap[s.key.toLowerCase()] = s
          }
        })
      }
      return DEFAULT_ROWS.map((d) => ({
        key: d.key,
        label: d.label,
        accountId: savedMap[d.key.toLowerCase()]?.accountId ?? null
      }))
    },

    async fetchAccounts(search = '') {
      try {
        this.accountsLoading = true
        const response = await this.$axios.get(axiosUrl.accounts.getAccounts, {
          params: { name: search, limit: 50, isSpecific: false }
        })
        const results =
          response.data.accountsManagements ||
          response.data.accounts ||
          response.data.ledgerGroups ||
          []

        // Ensure that currently selected accounts are always kept in the list
        // so Vuetify displays their names correctly even after a new search is made.
        const selectedIds = this.rows
          .map((r) => r.accountId)
          .filter((id) => id && typeof id === 'string')
        const selectedAccounts = this.accountsList.filter((a) => selectedIds.includes(a._id))
        const resultsMap = new Map(results.map((r) => [r._id, r]))

        // Merge preserved selected accounts and new search results
        selectedAccounts.forEach((sa) => {
          if (!resultsMap.has(sa._id)) {
            results.push(sa)
          }
        })

        this.accountsList = results
      } catch (error) {
        console.error('Error fetching accounts for VAS posting:', error)
      } finally {
        this.accountsLoading = false
      }
    }
  }
}
</script>

<style scoped>
.vas-header {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}
.vas-col {
  width: 280px;
  min-width: 280px;
  padding: 2px 4px;
}
.vas-row {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s;
}
.vas-row:last-child {
  border-bottom: none;
}
.vas-row:hover {
  background: #eff6ff !important;
}
.compact-select :deep(.v-input__control) {
  min-height: 36px !important;
}
.compact-select :deep(.v-select__slot) {
  font-size: 13px;
}
</style>
